// boardHistory — what stays on the Order Board and what has moved to History (Umer, 2026-10-06).
//
// Cancelled and Archive are columns of the board for the last two Ottawa calendar days (BOARD_DAYS): a card
// cancelled (or archived) on day D sits in its column on D and D+1 and is on the History page — Cancelled or
// Archived tab — from D+2 at 00:00 Ottawa time. The rule is read from each card's own persisted stamp
// (payment_board.cancelled_at, archived_at) against the Ottawa day: no timer, no write, the same answer after
// a refresh, a closed tab or a restart; the board re-reads it at Ottawa midnight (msToNextHistoryTick).
//
// TESTING WINDOW (FEATURES.boardHistoryTestMinutes, VITE_BOARD_HISTORY_TEST_MINUTES): with N > 0 the window
// is N minutes before now instead of two Ottawa days, re-read every 30 seconds, so the move can be watched.
// Every function takes the minutes as its last argument (default: the flag) so tests pass it explicitly.
import { addDaysToYmd, appDateStr, appDayStartMs, appFormat, appTodayStr } from './appTime';
import { FEATURES } from './features';

export const BOARD_DAYS = 2;
/** The testing window in minutes; 0 = the real two-day rule. */
export const HISTORY_TEST_MINUTES: number = FEATURES.boardHistoryTestMinutes;
const TEST_TICK_MS = 30_000;

/** The first Ottawa day still on the board ('YYYY-MM-DD'): yesterday, with BOARD_DAYS = 2. */
export const historyCutoffYmd = (today: string = appTodayStr()): string => addDaysToYmd(today, -(BOARD_DAYS - 1));
/** The boundary the board and History read on either side of: that day's midnight in Ottawa (epoch ms) —
 *  or, with the testing window, N minutes before now. */
export const historyCutoffMs = (today: string = appTodayStr(), nowMs: number = Date.now(), minutes: number = HISTORY_TEST_MINUTES): number =>
  minutes > 0 ? nowMs - minutes * 60_000 : appDayStartMs(historyCutoffYmd(today));
export const historyCutoffIso = (today: string = appTodayStr(), nowMs: number = Date.now(), minutes: number = HISTORY_TEST_MINUTES): string =>
  new Date(historyCutoffMs(today, nowMs, minutes)).toISOString();

/** On the board (true) or in History (false) for a stamp. A missing stamp is History: an old row from before
 *  the stamp existed. */
export const onBoard = (atMs: number | null | undefined, today: string = appTodayStr(), nowMs: number = Date.now(), minutes: number = HISTORY_TEST_MINUTES): boolean =>
  atMs != null && atMs >= historyCutoffMs(today, nowMs, minutes);

/** The Ottawa day a card with this stamp moves to History ('YYYY-MM-DD'). */
export const movesToHistoryOn = (atMs: number): string => addDaysToYmd(appDateStr(atMs), BOARD_DAYS);

/** "today" / "tomorrow" / "Wed, Oct 7", in Ottawa time. A day already passed reads "today". */
export const historyDayLabel = (ymd: string, today: string = appTodayStr()): string =>
  ymd <= today ? 'today' : ymd === addDaysToYmd(today, 1) ? 'tomorrow' : appFormat(appDayStartMs(ymd), { weekday: 'short', month: 'short', day: 'numeric' });

/** When a card with this stamp moves to History, as the popup says it: the day — or, with the testing
 *  window, the minute ("4:12 PM"). */
export const movesToHistoryLabel = (atMs: number, today: string = appTodayStr(), minutes: number = HISTORY_TEST_MINUTES): string =>
  minutes > 0 ? appFormat(atMs + minutes * 60_000, { hour: 'numeric', minute: '2-digit' }) : historyDayLabel(movesToHistoryOn(atMs), today);

/** "two days" — or "2 minutes" with the testing window; for the texts that name the rule. */
export const historyWindowLabel = (minutes: number = HISTORY_TEST_MINUTES): string =>
  minutes > 0 ? `${minutes} minute${minutes === 1 ? '' : 's'}` : 'two days';

/** Milliseconds until the board re-reads the rule: just after the next Ottawa midnight — or 30 seconds with
 *  the testing window. */
export const msToNextHistoryTick = (nowMs: number = Date.now(), minutes: number = HISTORY_TEST_MINUTES): number =>
  minutes > 0 ? TEST_TICK_MS : Math.max(1000, appDayStartMs(addDaysToYmd(appDateStr(nowMs), 1)) - nowMs + 1000);
/** @deprecated name kept for the tests of 2026-10-06; the same as msToNextHistoryTick with the real rule. */
export const msToNextOttawaMidnight = (nowMs: number = Date.now()): number => msToNextHistoryTick(nowMs, 0);

// ── Where a finished card is ──────────────────────────────────────────────────────────────────────
// A card found by search, or opened from History, must say which column it came from and where it is now
// (Umer, 2026-10-06: "we see the card detail but don't know from which column it is"). Archived wins over
// cancelled — an archived cancelled card is History › Archived, never both; a finished card with no stamp is
// History (an old row from before the stamp existed).
export type HistoryTab = 'cancelled' | 'archived';
export type CardPlace = { where: 'board' | 'history'; tab: HistoryTab | null; since: number | null };
export const HISTORY_TAB_LABEL: Record<HistoryTab, string> = { cancelled: 'Cancelled', archived: 'Archived' };

export const cardPlace = (
  card: { archived?: boolean; stage?: string; archivedAt?: number | null; cancelledAt?: number | null },
  today: string = appTodayStr(), nowMs: number = Date.now(), minutes: number = HISTORY_TEST_MINUTES,
): CardPlace => {
  if (card.archived) return { where: onBoard(card.archivedAt, today, nowMs, minutes) ? 'board' : 'history', tab: 'archived', since: card.archivedAt ?? null };
  if (card.stage === 'cancelled') return { where: onBoard(card.cancelledAt, today, nowMs, minutes) ? 'board' : 'history', tab: 'cancelled', since: card.cancelledAt ?? null };
  return { where: 'board', tab: null, since: null };
};

/** "History › Cancelled" / "History › Archived" once it is off the board; "Cancelled column" / "Archive column"
 *  while it still is; nothing for a live card. */
export const placeLabel = (p: CardPlace): string =>
  !p.tab ? '' : p.where === 'history' ? `History › ${HISTORY_TAB_LABEL[p.tab]}` : p.tab === 'cancelled' ? 'Cancelled column' : 'Archive column';

/** "Oct 4" — or "Oct 4, 6:12 PM" with the time — in Ottawa time. */
export const whenLabel = (ms: number, withTime = false): string =>
  appFormat(ms, withTime ? { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' } : { month: 'short', day: 'numeric' });
/** "since Oct 4" / "since Oct 4, 6:12 PM"; nothing without a stamp. */
export const sinceLabel = (ms: number | null | undefined, withTime = false): string => (ms == null ? '' : `since ${whenLabel(ms, withTime)}`);
